import { useCallback, useEffect, useRef } from 'react'
import { useSocketContext } from './socket-context'
import type { ServerToClientEvents } from './types'

/**
 * Hook to subscribe to socket events
 */
export function useSocketEvent<K extends keyof ServerToClientEvents>(
  event: K,
  callback: ServerToClientEvents[K]
): void {
  const { socket } = useSocketContext()
  const callbackRef = useRef(callback)

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    if (!socket) return

    const handler = (...args: Parameters<ServerToClientEvents[K]>) => {
      // @ts-expect-error - TypeScript can't infer the correct callback signature
      callbackRef.current(...args)
    }

    // Use type assertion to avoid complex socket.io generic inference
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    socket.on(event, handler as any)

    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      socket.off(event, handler as any)
    }
  }, [socket, event])
}

/**
 * Hook to get socket connection state
 */
export function useSocketConnection() {
  const { isConnected, connectionError, emit, socket } = useSocketContext()
  return { isConnected, connectionError, emit, socketId: socket?.id }
}

/**
 * Hook to emit socket events
 */
export function useSocketEmit() {
  const { emit, isConnected } = useSocketContext()

  const safeEmit = useCallback(
    <K extends keyof import('./types').ClientToServerEvents>(
      event: K,
      payload: Parameters<import('./types').ClientToServerEvents[K]>[0]
    ) => {
      if (isConnected) {
        emit(event, payload)
        return true
      }
      return false
    },
    [emit, isConnected]
  )

  return { emit: safeEmit, isConnected }
}
